import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { isRecoveryCallback } from "@/lib/auth/recovery-link";
import { Sparkles, Zap, Swords, Egg, Map, Trophy, Shield, Store, Wand2 } from "lucide-react";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { LegalFooter } from "@/components/LegalFooter";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/game/Panel";
import { Pill } from "@/components/game/Badges";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Xenomons – Dein Xenomon wartet" },
      { name: "description", content: "Erschaffe ein einzigartiges KI-Xenomon, ziehe es groß und kämpfe dich Stage für Stage durch 20 Neon-Zonen." },
      { property: "og:title", content: "Xenomons – Dein Xenomon wartet" },
      { property: "og:description", content: "Erschaffe ein einzigartiges KI-Xenomon, ziehe es groß und kämpfe dich Stage für Stage durch 20 Neon-Zonen." },
      { property: "og:url", content: "https://xenomons.online/" },
      { name: "twitter:title", content: "Xenomons – Dein Xenomon wartet" },
      { name: "twitter:description", content: "Erschaffe ein einzigartiges KI-Xenomon, ziehe es groß und kämpfe dich Stage für Stage durch 20 Neon-Zonen." },
    ],
    links: [
      { rel: "canonical", href: "https://xenomons.online/" },
      { rel: "alternate", hrefLang: "de", href: "https://xenomons.online/" },
      { rel: "alternate", hrefLang: "x-default", href: "https://xenomons.online/" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Xenomons",
          url: "https://xenomons.online/",
          description: "Cyber creature battler with AI-generated Xenomons.",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "VideoGame",
          name: "Xenomons",
          url: "https://xenomons.online/",
          description: "Raise, equip and battle AI-generated cyber creatures across 20 neon zones.",
          genre: ["Creature Battler", "Auto Battler", "RPG"],
          applicationCategory: "Game",
          operatingSystem: "Web",
        }),
      },
    ],
  }),
  beforeLoad: async () => {
    if (typeof window === "undefined") return;
    // A password-recovery link signs the visitor in. Bouncing them to /game
    // here would swallow the reset: they end up playing with the old password
    // and never see the form. Stand down and let the root route send them to
    // /reset-password once Supabase has read the fragment.
    if (isRecoveryCallback(window.location.hash, window.location.search)) return;
    const { data } = await supabase.auth.getUser();
    if (data.user) throw redirect({ to: "/game" });
  },
  component: Landing,
});

/** Abschnittsüberschrift: global schon Lilita One mit Ink-Schatten, hier nur die Größe. */
const H2 = "text-2xl sm:text-3xl";

function Landing() {
  const { t } = useTranslation("landing");
  const features = [
    { icon: Sparkles, key: "unique" },
    { icon: Swords, key: "combat" },
    { icon: Wand2, key: "moves" },
    { icon: Shield, key: "growth" },
    { icon: Egg, key: "hatchery" },
    { icon: Zap, key: "evolution" },
    { icon: Map, key: "expeditions" },
    { icon: Store, key: "market" },
    { icon: Trophy, key: "achievements" },
  ] as const;

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Einzige Hintergrund-Textur der Designsprache: Halftone-Raster, keine Scanlines, kein Glow. */}
      <div className="halftone pointer-events-none absolute inset-0 opacity-70" aria-hidden />

      <header className="relative z-10 mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-5 sm:px-6 sm:py-6">
        <span className="font-display text-xl tracking-[0.04em] ink-shadow sm:text-2xl">
          XENOMONS<span className="text-primary">.</span>
        </span>
        <div className="flex items-center gap-2">
          <LanguageSwitcher variant="ghost" size="sm" />
          <Button asChild variant="secondary" size="sm">
            <Link to="/auth">{t("header.login")}</Link>
          </Button>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-6xl px-4 pb-24 pt-8 sm:px-6 sm:pt-12">
        <section className="text-center">
          <Pill
            className="px-3 py-0.5 text-[11px]"
            style={{ background: "var(--accent)", color: "var(--foreground)" }}
          >
            {t("hero.badge")}
          </Pill>
          <h1 className="mx-auto mt-4 max-w-3xl text-[2.5rem] leading-[1.05] [text-shadow:4px_4px_0_var(--ink)] sm:text-6xl md:text-7xl">
            {t("hero.title_before")}
            <span className="text-primary">{t("hero.title_highlight")}</span>
            {t("hero.title_after")}
            <br />
            {t("hero.title_line2")}
          </h1>
          <p className="mx-auto mt-6 max-w-[52ch] text-base leading-relaxed text-muted-foreground sm:text-lg">
            {t("hero.subtitle")}
          </p>
          {/* Der einzige orange Knopf der Seite. */}
          <Button asChild variant="gradient" size="lg" className="mt-9 min-h-14 px-8 text-lg">
            <Link to="/auth">{t("hero.cta")}</Link>
          </Button>
        </section>

        <section className="mt-16 md:mt-24" aria-labelledby="features-heading">
          <h2 id="features-heading" className="sr-only">Features</h2>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((f) => (
              <Panel key={f.key} contentClassName="p-5">
                <div className="grid h-10 w-10 place-items-center rounded-md border-2 border-ink bg-primary text-ink">
                  <f.icon className="h-5 w-5" strokeWidth={2.5} />
                </div>
                <h3 className="mt-3 text-lg">{t(`features.${f.key}.title`)}</h3>
                <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground [overflow-wrap:anywhere]">
                  {t(`features.${f.key}.text`)}
                </p>
              </Panel>
            ))}
          </div>
        </section>

        <section className="mx-auto mt-16 max-w-3xl md:mt-24">
          <h2 className={H2}>Was ist Xenomons?</h2>
          <Panel grid className="mt-5" contentClassName="space-y-4 p-5 text-[15px] leading-relaxed text-muted-foreground sm:p-6">
            <p>
              <strong className="text-foreground">Xenomons</strong> ist ein kostenloses Browser-Game,
              in dem du KI-generierte Cyber-Kreaturen sammelst, großziehst und in einem strategischen
              Auto-Battler kämpfen lässt. Jede Kreatur wird per KI mit einzigartigem Aussehen, Lore
              und Persönlichkeit erschaffen – kein Xenomon gleicht dem anderen.
            </p>
            <p>
              Du spielst direkt im Browser, ohne Download und ohne Installation. Ob am Handy in der Bahn
              oder abends am PC: dein Fortschritt liegt sicher in der Cloud und läuft nahtlos auf allen
              Geräten. Im Kampf zählt die Vorbereitung: Du entscheidest, welche Attacken dein Xenomon
              lernt und in welchen Slots sie liegen. Eine neue Stage spielst du selbst – dort tippst
              du deine Attacken an, sobald sie bereit sind.
            </p>
            <p>
              Zwischen den Kämpfen baust du dein Xenomon aus: Ausrüstung anlegen, Attacken tauschen,
              Eier ausbrüten, Expeditionen starten – und ab Level 15 die Entfaltung in eine stärkere
              Form wählen. Wer strategisch plant, gewinnt selbst gegen deutlich stärkere Gegner –
              Typen-Wissen schlägt rohe Werte.
            </p>
          </Panel>
        </section>

        <section className="mx-auto mt-16 max-w-4xl md:mt-24">
          <h2 className={H2}>Für wen ist Xenomons?</h2>
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <Panel contentClassName="p-5">
              <h3 className="text-lg">Creature-Collector-Fans</h3>
              <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">
                Wenn du Sammel-RPGs magst und einzigartige Kreaturen suchst, findest du hier ein
                Xenomon, das es nur ein einziges Mal gibt – deins.
              </p>
            </Panel>
            <Panel contentClassName="p-5">
              <h3 className="text-lg">Auto-Battler-Fans</h3>
              <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">
                Strategie statt Klick-Marathon: Attacken, Slots und Typen-Konter entscheiden.
                Expeditionen und Brutzeiten laufen weiter, während du offline bist.
              </p>
            </Panel>
            <Panel contentClassName="p-5">
              <h3 className="text-lg">Sammler und Züchter</h3>
              <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">
                Züchten in der Brutkammer, Eier von besiegten Bossen und vier Raritätsstufen bei der
                Ausrüstung – Tiefe für alle, die gezielt auf bestimmte Typ-Kombinationen aus sind.
              </p>
            </Panel>
            <Panel contentClassName="p-5">
              <h3 className="text-lg">Cyberpunk-Fans</h3>
              <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">
                Neon-Zonen, Glitch-Ästhetik und ein Setting zwischen Cyber-Habitat und
                KI-Experiment – für alle, die Sci-Fi-Vibes lieben.
              </p>
            </Panel>
          </div>
        </section>

        <section className="mx-auto mt-16 max-w-3xl md:mt-24">
          <h2 className={H2}>So funktioniert's</h2>
          <ol className="mt-5 space-y-3">
            <li>
              <Panel contentClassName="p-5">
                <h3 className="text-lg">1. Xenomon erwecken</h3>
                <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">
                  Registriere dich kostenlos und lass die KI dein erstes einzigartiges Xenomon
                  erschaffen – Look, Name und Basis-Stats inklusive.
                </p>
              </Panel>
            </li>
            <li>
              <Panel contentClassName="p-5">
                <h3 className="text-lg">2. Moves lernen und Slots belegen</h3>
                <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">
                  7 Move-Slots: 6 für normale Attacken, Slot 7 für die Ultimate ab Level 30. Neue
                  Attacken lernt dein Xenomon zu bestimmten Stufen – zum Start trägt es nur seinen
                  eingebauten Basis-Angriff. Achte auf den Typen-Konter: Er hängt an der Attacke,
                  mit der du zuschlägst.
                </p>
              </Panel>
            </li>
            <li>
              <Panel contentClassName="p-5">
                <h3 className="text-lg">3. Ausrüsten, Züchten, Erforschen</h3>
                <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">
                  Ausrüstung anlegen, Eier ausbrüten, Expeditionen starten, Entfaltung wählen – jede
                  Aktion baut dein Team langfristig aus.
                </p>
              </Panel>
            </li>
          </ol>
          <p className="mt-5 text-sm text-muted-foreground">
            Ausführlich Schritt für Schritt:{" "}
            <Link to="/how-to-play" className="font-extrabold text-primary hover:underline">
              Zur kompletten Xenomons-Anleitung
            </Link>
            .
          </p>
        </section>

        <section className="mx-auto mt-16 max-w-3xl md:mt-24">
          <h2 className={H2}>Häufig gefragt</h2>
          <div className="mt-5 space-y-3">
            <Panel contentClassName="p-5">
              <h3 className="text-base">Ist Xenomons wirklich kostenlos?</h3>
              <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">
                Ja, komplett Free-to-Play. Optionale Gold-Pakete beschleunigen den Fortschritt,
                sind aber nicht nötig.
              </p>
            </Panel>
            <Panel contentClassName="p-5">
              <h3 className="text-base">Brauche ich einen Download?</h3>
              <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">
                Nein. Xenomons läuft direkt im Browser – auf Handy, Tablet und PC.
              </p>
            </Panel>
            <Panel contentClassName="p-5">
              <h3 className="text-base">Wie funktioniert das Kampfsystem?</h3>
              <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">
                Auto-Battler mit 7 Move-Slots pro Xenomon (6 normale Moves + 1 Ultimate). Fünf Typen im
                Konter-Ring, Gegner treten nacheinander im Duell an – und eine Stage, die du zum
                ersten Mal betrittst, spielst du von Hand.
              </p>
            </Panel>
          </div>
          <p className="mt-5 text-sm text-muted-foreground">
            Alle Antworten:{" "}
            <Link to="/faq" className="font-extrabold text-primary hover:underline">
              Zur FAQ
            </Link>
            .
          </p>
        </section>

        <section className="mt-16 text-center md:mt-24">
          <h2 className={H2}>Bereit für dein erstes Xenomon?</h2>
          <p className="mx-auto mt-4 max-w-[52ch] text-muted-foreground">
            Kostenlos, direkt im Browser, kein Download. Erschaffe jetzt deine einzigartige
            Cyber-Kreatur.
          </p>
          {/* Zweiter Weg zur selben Aktion — bewusst NICHT orange: der Hero-CTA oben ist der eine. */}
          <Button asChild variant="secondary" size="lg" className="mt-7 min-h-12 px-8 text-base">
            <Link to="/auth">Jetzt kostenlos spielen</Link>
          </Button>
        </section>
      </main>
      <LegalFooter />
    </div>
  );
}
